/**
 * /ar — Customer invoices (Sales).
 *
 * Single-purpose page: lists AR invoices, supports multi-select →
 * Record payment via the existing MultiPaymentDialog flow.
 *
 * Split out of the old combined /invoices page (April 2026) so each
 * side of the ledger has its own dedicated surface and the section
 * vocabulary is unambiguous: "Invoices" here always means customer
 * invoices, never vendor bills.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@mui/material";

import { useShortcut } from "@/lib/shortcuts";
import { HelpHint } from "@/components/HelpHint";
import { MultiPaymentDialog, toInvoiceRows } from "@/components/MultiPaymentDialog";
import {
  arInvoicesListQueryOptions,
  partiesListQueryOptions,
} from "@/lib/queries";
import { InvoicesGrid } from "@/grids/InvoicesGrid";
import type { ARInvoice, Party } from "@/lib/types";

interface CommonRow {
  id: string;
  party_id: string;
  party_name: string | null;
  outstanding: number;
  currency_code: string;
  status: string;
}

export const Route = createFileRoute("/_auth/ar")({
  component: ARInvoicesPage,
});

function ARInvoicesPage() {
  const navigate = useNavigate();

  // `n` opens "New invoice" — mirrors the header button.
  useShortcut("n", () => navigate({ to: "/invoices/ar/new" }), {
    label: "New invoice",
  });

  const [selectedRows, setSelectedRows] = useState<CommonRow[]>([]);
  const [payOpen, setPayOpen] = useState(false);

  const arInvoices = useQuery(arInvoicesListQueryOptions({}));
  const allParties = useQuery(partiesListQueryOptions());

  const partiesById = useMemo(() => {
    const m: Record<string, Party> = {};
    (allParties.data ?? []).forEach((p) => {
      m[p.id] = p;
    });
    return m;
  }, [allParties.data]);

  // Selection validation: must all be same customer + currency + outstanding > 0
  const selectionValid = useMemo(() => {
    if (selectedRows.length === 0) return false;
    const partyIds = new Set(selectedRows.map((r) => r.party_id));
    const currencies = new Set(selectedRows.map((r) => r.currency_code));
    const hasOutstanding = selectedRows.some((r) => r.outstanding > 0);
    return partyIds.size === 1 && currencies.size === 1 && hasOutstanding;
  }, [selectedRows]);

  const selectionWarn = useMemo(() => {
    if (selectedRows.length === 0) return null;
    const partyIds = new Set(selectedRows.map((r) => r.party_id));
    const currencies = new Set(selectedRows.map((r) => r.currency_code));
    if (partyIds.size > 1) return "Selected invoices must be from the same customer.";
    if (currencies.size > 1) return "Selected invoices must share the same currency.";
    if (!selectedRows.some((r) => r.outstanding > 0)) return "No outstanding balance on selected invoices.";
    return null;
  }, [selectedRows]);

  const selectedPartyId = selectedRows[0]?.party_id ?? "";
  const selectedPartyName = selectedRows[0]?.party_name ?? "";
  const selectedCurrency = selectedRows[0]?.currency_code ?? "USD";
  const selectedTotalOutstanding = selectedRows.reduce((s, r) => s + r.outstanding, 0);

  const selectedARInvoices = useMemo<ARInvoice[]>(() => {
    if (!arInvoices.data) return [];
    const ids = new Set(selectedRows.map((r) => r.id));
    return arInvoices.data.filter((inv) => ids.has(inv.id));
  }, [selectedRows, arInvoices.data]);

  const fmtMoney = (n: number, ccy = "USD") =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: ccy,
      maximumFractionDigits: 2,
    }).format(n);

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Invoices</h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Customer invoices · click an invoice # to open · totals in the row
          </span>
          <HelpHint docPath="invoices/ar.md" label="AR invoices help" />
          <div className="flex-1" />
          <Link
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            to={"/invoices/ar/new" as any}
            className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 font-medium"
          >
            + New invoice
          </Link>
        </div>
      </div>

      {/* Selection action bar */}
      {selectedRows.length > 0 && (
        <div className="bg-blue-50 border-b border-blue-200 px-6 py-2 flex items-center gap-4">
          <span className="text-sm font-medium text-blue-900">
            {selectedRows.length} invoice{selectedRows.length !== 1 ? "s" : ""} selected
            {selectionValid && (
              <span className="ml-2 text-blue-700">
                · {fmtMoney(selectedTotalOutstanding, selectedCurrency)} outstanding
              </span>
            )}
          </span>
          {selectionWarn && (
            <span className="text-xs text-amber-700 bg-amber-100 rounded px-2 py-0.5">{selectionWarn}</span>
          )}
          <div className="flex-1" />
          <Button
            variant="contained"
            size="small"
            disabled={!selectionValid}
            onClick={() => setPayOpen(true)}
          >
            Record payment
          </Button>
          <Button variant="outlined" size="small" onClick={() => setSelectedRows([])}>
            Clear
          </Button>
        </div>
      )}

      {selectionValid && (
        <MultiPaymentDialog
          open={payOpen}
          onClose={() => {
            setPayOpen(false);
            setSelectedRows([]);
          }}
          invoices={toInvoiceRows(selectedARInvoices)}
          partyId={selectedPartyId}
          partyDisplayName={selectedPartyName || selectedPartyId}
          currency={selectedCurrency}
        />
      )}

      <div className="flex-1 px-6 py-4 overflow-hidden">
        <InvoicesGrid
          direction="ar"
          invoices={arInvoices.data ?? []}
          partiesById={partiesById}
          loading={arInvoices.isLoading || allParties.isLoading}
          onSelectionChange={(rows) => setSelectedRows(rows as unknown as CommonRow[])}
        />
      </div>
    </div>
  );
}
